import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAgent } from '../../context/AgentContext';
import { useAuth } from '../../context/AuthContext';
import AgentPanel from './AgentPanel';
import AgentFullScreen from './AgentFullScreen';

/**
 * AgentWidget — outer wrapper, always rendered on every authenticated page.
 *
 * Renders:
 *   - Collapsed floating logo button (always visible when isOpen=false)
 *   - AgentPanel when isOpen=true and !isFullScreen
 *   - AgentFullScreen when isOpen=true and isFullScreen
 *
 * Reads page context from current route for system prompt injection.
 */
const AgentWidget = () => {
  const { isAuthenticated } = useAuth();
  const { isOpen, isFullScreen, openPanel, closePanel, toggleFullScreen, killSession } = useAgent();
  const location = useLocation();

  // Derive page context from current route
  const pageContext = useMemo(() => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);

    if (path.startsWith('/clients/') && parts[1]) {
      return { page: 'client_detail', client_id: parts[1], portfolio_id: null };
    }
    if (path.startsWith('/portfolio/') && parts[1]) {
      return { page: 'portfolio_detail', client_id: null, portfolio_id: parts[1] };
    }
    if (path === '/dashboard') {
      return { page: 'dashboard', client_id: null, portfolio_id: null };
    }
    if (path === '/portfolio') {
      return { page: 'portfolio_list', client_id: null, portfolio_id: null };
    }
    if (path === '/clients') {
      return { page: 'clients_list', client_id: null, portfolio_id: null };
    }
    if (path === '/documents') {
      return { page: 'documents', client_id: null, portfolio_id: null };
    }
    return { page: 'other', client_id: null, portfolio_id: null };
  }, [location.pathname]);

  // Don't render on public routes
  if (!isAuthenticated) return null;

  const handleOpen = async () => {
    openPanel();
    // Auto-create session if widget was just opened fresh
  };

  const handleKillSession = async () => {
    await killSession();
    closePanel();
  };

  const handleMinimize = () => closePanel();
  const handleFullScreen = () => toggleFullScreen();
  const handleExitFullScreen = () => toggleFullScreen();

  // Full screen state
  if (isOpen && isFullScreen) {
    return (
      <AgentFullScreen
        pageContext={pageContext}
        onExitFullScreen={handleExitFullScreen}
        onKillSession={handleKillSession}
      />
    );
  }

  return (
    <>
      {/* Collapsed floating button (always visible) */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-[9999] w-13 h-13 rounded-full bg-accent shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow"
          style={{ width: '52px', height: '52px' }}
          title="Open Vasari Agent"
          aria-label="Open Vasari Agent"
        >
          <VasariIcon />
        </button>
      )}

      {/* Expanded panel */}
      {isOpen && !isFullScreen && (
        <>
          <AgentPanel
            pageContext={pageContext}
            onMinimize={handleMinimize}
            onFullScreen={handleFullScreen}
            onKillSession={handleKillSession}
          />
          {/* Floating logo still visible (behind panel, acts as collapse) */}
          <button
            onClick={handleMinimize}
            className="fixed bottom-6 right-6 z-[9998] w-13 h-13 rounded-full bg-accent shadow-lg flex items-center justify-center"
            style={{ width: '52px', height: '52px' }}
            aria-label="Minimize agent"
          >
            <VasariIcon />
          </button>
        </>
      )}
    </>
  );
};

/* SVG logo mark — white on blue accent background */
const VasariIcon = () => (
  <svg width="26" height="26" viewBox="0 0 8689 9614" fill="white" xmlns="http://www.w3.org/2000/svg">
    <rect x="5905" y="1006" width="1935" height="3121" transform="rotate(90 5905 1006)" />
    <rect x="4792" y="0" width="2819" height="895" transform="rotate(90 4792 0)" />
    <rect x="5566" y="548" width="916" height="2445" transform="rotate(90 5566 548)" />
    <rect x="3123" y="2900" width="1935" height="3121" transform="rotate(90 3123 2900)" />
    <rect x="2010" y="1894" width="3371" height="895" transform="rotate(90 2010 1894)" />
    <rect x="2784" y="2442" width="916" height="2445" transform="rotate(90 2784 2442)" />
    <rect x="8689" y="2928" width="1935" height="3121" transform="rotate(90 8689 2928)" />
    <rect x="7576" y="1922" width="3371" height="895" transform="rotate(90 7576 1922)" />
    <rect x="8350" y="2470" width="916" height="2445" transform="rotate(90 8350 2470)" />
    <rect x="2782" y="8608" width="1935" height="3121" transform="rotate(-90 2782 8608)" />
    <rect x="3895" y="9614" width="2900" height="895" transform="rotate(-90 3895 9614)" />
    <rect x="3121" y="9066" width="916" height="2445" transform="rotate(-90 3121 9066)" />
    <rect x="5566" y="6742" width="1935" height="3121" transform="rotate(-90 5566 6742)" />
    <rect x="6679" y="7748" width="3371" height="895" transform="rotate(-90 6679 7748)" />
    <rect x="5905" y="7200" width="916" height="2445" transform="rotate(-90 5905 7200)" />
    <rect y="6714" width="1935" height="3121" transform="rotate(-90 0 6714)" />
    <rect x="1113" y="7720" width="3371" height="895" transform="rotate(-90 1113 7720)" />
    <rect x="339" y="7172" width="916" height="2445" transform="rotate(-90 339 7172)" />
  </svg>
);

export default AgentWidget;
